import React, { useContext, useCallback, FC, Fragment, useMemo } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import noop from 'lodash/noop';
import partialRight from 'lodash/partialRight';
import set from 'lodash/set';
import styled from 'styled-components';
import { produce } from 'immer';

import TrashIcon from '@ad-scripts/presentation/components/common/icons/trash';
import FormContext from 'src/presentation/contexts/FormContext';
import getErrorMessage from 'src/presentation/components/forms/getErrorMessage';
import { Button } from 'src/presentation/components/agent_desktop_components';
import {
  ErrorWrapper,
  InputRanges,
  StartStopSelectorsWrapper,
} from 'src/presentation/components/pages/shared/HoursTable/TimeRange';

type TimeRangeProps = {
  'data-aid'?: string;
  attr: string;
  disabled?: boolean;
  onAdd?: Function;
  onBlur?: Function;
  onChange?: Function;
  onDelete?: Function;
  readOnly?: boolean;
  value?: any[];
};

export const TimeRanges: FC<TimeRangeProps> = ({
  'data-aid': dataAid,
  attr,
  onAdd,
  onBlur = noop,
  onChange,
  onDelete,
  disabled,
  readOnly,
  value,
}) => {
  const { errors, workingState, updateState } = useContext(FormContext);
  const timeRanges = useMemo(() => [...(value || get(workingState, attr, []))], [attr, value, workingState]);

  const onDeleteClick = useCallback(
    index => {
      if (onDelete) {
        onDelete(index);
        return;
      }
      timeRanges.splice(index, 1);
      updateState(set({}, attr, timeRanges));
      onBlur({ currentState: produce(workingState, draftState => set(draftState, attr, timeRanges)) });
    },
    [attr, onBlur, onDelete, timeRanges, updateState, workingState]
  );

  const onAddClick = useCallback(() => {
    const newTimeRange = [...timeRanges, { start: '', end: '' }];
    if (onAdd) {
      onAdd(newTimeRange);
      return;
    }
    updateState(set(workingState, attr, newTimeRange));
  }, [attr, onAdd, timeRanges, updateState, workingState]);

  const controls = timeRanges.map((range, index) => {
    const rangeAttr = `${attr}[${index}]`;
    const startAttr = `${rangeAttr}.start`;
    const endAttr = `${rangeAttr}.end`;

    const onTimeChange = ({ attr, fullAttr, value }) => {
      if (onChange) {
        onChange({ attr, fullAttr, value, index });
        return;
      }
      updateState(set({}, fullAttr, value));
      onBlur({ currentState: produce(workingState, draftState => set(draftState, fullAttr, value)) });
    };

    const errorStart = find(errors, error => error.attr === `${attr}[${index}].start`);
    const errorEnd = find(errors, error => error.attr === `${attr}[${index}].end`);

    const errorMessage =
      errorStart && errorEnd
        ? 'Both start time and end time need to be filled'
        : errorStart || errorEnd
        ? getErrorMessage(errorStart || errorEnd, '')
        : '';

    const baseDataAid = `${dataAid}[${index}]`;
    return (
      <Fragment key={rangeAttr}>
        <RangeWithDelete readOnly={readOnly} data-aid={baseDataAid}>
          <InputRanges
            attr={rangeAttr}
            data-aid={baseDataAid}
            disabled={disabled || readOnly}
            onTimeChange={partialRight(onTimeChange, range)}
            row={range}
            separatorText=""
            showDeleteButton
            startAttr={startAttr}
            startLabel="Start"
            stopAttr={endAttr}
            stopAttrName="end"
            stopLabel="End"
          />
          {!readOnly && !disabled && (
            <DeleteButtonContainer>
              <StyledTrashIcon data-aid={`${dataAid}[${index}]-deleteButton`} onClick={() => onDeleteClick(index)} />
            </DeleteButtonContainer>
          )}
        </RangeWithDelete>
        {errorMessage && (
          <ErrorWrapper key={`${rangeAttr}.error`}>
            <ErrorLabel data-aid={`${dataAid}[${index}]-error`}>{errorMessage}</ErrorLabel>
          </ErrorWrapper>
        )}
      </Fragment>
    );
  });
  return (
    <>
      {controls}
      {!readOnly && !disabled && (
        <ActionPanel>
          <AddTimeRangeButton data-aid={`${dataAid}-addTimeRange`} type={Button.Types.SECONDARY} onClick={onAddClick}>
            Add Time Range
          </AddTimeRangeButton>
        </ActionPanel>
      )}
    </>
  );
};

const ErrorLabel = styled.div`
  color: ${p => p.theme.colors.red400};
`;

const DeleteButtonContainer = styled.div`
  display: flex;
  width: 26px;
  margin-bottom: -16px;
  justify-content: flex-end;
`;

const StyledTrashIcon = styled(TrashIcon)`
  cursor: pointer;
  height: 16px;
  width: 16px;
`;

const RangeWithDelete = styled.div`
  align-items: center;
  display: flex;
  width: 100%;

  ${StartStopSelectorsWrapper} {
    align-items: flex-start;
  }
`;

const ActionPanel = styled.div`
  display: flex;
  justify-content: center;
`;

const AddTimeRangeButton = styled(Button)`
  width: 160px;
`;
